import React, { useState, useEffect } from "react";
import { Mail, RefreshCcw, Send, LogOut, CheckCircle, ShieldAlert, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useNavigate } from "react-router-dom";
import { sendEmailVerification } from "firebase/auth";
import Navigation from "../components/Navigation";
import FooterSection from "../components/FooterSection";
import "./VerifyEmail.css";

const VerifyEmail = () => {
  const { currentUser, userData, refreshUser, logout } = useAuth();
  const { showAlert } = useToast();
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Redirection via Global Arrival Gate (Exempting Caregivers)
    if (currentUser?.emailVerified || userData?.role === "caregiver") {
      navigate("/", { replace: true });
    }
  }, [currentUser?.emailVerified, userData?.role, navigate]);

  const handleResend = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      await sendEmailVerification(currentUser);
      showAlert("Verification email transmitted. Please check your medical-grade inbox.", "success");
      setTimer(60); 
    } catch (error) {
      showAlert("Email Transmission Failure: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await refreshUser();
    } catch (error) {
      console.error("Refresh error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  return (
    <div className="ve-portal">
      <Navigation />
      <div className="verify-container">
      <div className="verify-card">
        {/* Tactical HUD Corner Brackets */}
        <div className="hud-corner-tl"></div>
        <div className="hud-corner-br"></div>

        <div className="verify-logo-wrapper">
          <ShieldAlert size={32} strokeWidth={2.5} />
        </div>
        
        <h1>Authorize Identity</h1>
        <p className="subtitle">
          Identity verification sequence initiated for <strong>{currentUser?.email}</strong>. 
          Please authorize your clinical session to gain dashboard access.
        </p>

        <div className="verify-status-hud">
          <div className={`status-row ${currentUser?.emailVerified ? 'verified' : 'pending'}`}>
            {currentUser?.emailVerified ? (
              <CheckCircle size={20} strokeWidth={3} />
            ) : (
              <RefreshCcw className="spinning-icon" size={20} strokeWidth={3} />
            )}
            <span>{currentUser?.emailVerified ? "SESSION_AUTHORIZED" : "AWAITING_AUTHORIZATION..."}</span>
          </div>
        </div>

        <div className="verify-actions">
          <button 
            className="verify-btn primary" 
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCcw size={18} className={loading ? "spinning-icon" : ""} />
            {loading ? "SYNCING_IDENTITY..." : "Refresh Authorization Status"}
          </button>

          <button 
            className="verify-btn secondary" 
            onClick={handleResend}
            disabled={loading || timer > 0}
          >
            <Send size={18} />
            {timer > 0 ? `RETRY_IN_${timer}S` : "Resend Authorization Link"}
          </button>

          <button className="verify-btn ghost" onClick={logout}>
            <LogOut size={16} />
            ABORT_SESSION
          </button>
        </div>

        <div className="verify-footer">
          <p>
            <AlertCircle size={14} /> 
            <span>
              SECURITY_NOTICE: Link expires in 24h. Check clinical spam folder if no transmission is detected within 60s.
            </span>
          </p>
        </div>
      </div>

      {loading && (
        <div className="verify-loading-overlay">
          <div className="loader-box">
            <RefreshCcw size={48} className="spinning-icon" />
            <p>SYNCHRONIZING_IDENTITY_ASSETS...</p>
          </div>
        </div>
      )}
      </div>
      <FooterSection />
    </div>
  );
};

export default VerifyEmail;
